/**
 * @file CustomerOrderModal.js
 * @version 1.23.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-18
 * @modified 2026-11-03
 *
 * 1.23.0 (2026-11-03, Cam — "record the book over on the order as we go"):
 * a "Booked over" pill (BookedOverBadge) beside the other pills — the order's
 * invoice moved to another date, or split; each part opens its invoice.
 *
 * 1.22.0 (2026-10-30, Cam — rep order requests, 29 Oct): a rep (on their
 * phone or laptop) gets "Request a change" in the footer — the request form
 * filled in from this order, sent to the office (RepChangeRequestModal); never
 * an edit. Greyed with the reason on an archived or invoiced order.
 *
 * 1.21.0 (2026-10-28, Cam — units clear before November's invoices): each
 * line's unit price says what it is per (PriceWithUnit — "£400.00/T",
 * "£10.00/25kg bag" with its figure a tonne, "unit not set"); a line with no
 * supplied quantity says "Not recorded yet — needed before invoicing" (or
 * "Not recorded" once invoiced) rather than a dash (utils/unitPriceLabel).
 * A bulk line with no weight reads in tonnes (item_sales_account_code).
 * 1.20.0 (2026-10-28, Cam — rep mobile review): on a rep's phone the top
 * says where the order has got to in words — the Sales Orders card's chips
 * (utils/repOrderCard) — instead of eight hover-only icons, and the lines
 * are the card's line blocks (RepLineBlock), each with its PO as a button,
 * instead of a table that broke the PO number over two lines. Desktop
 * unchanged.
 *
 * 1.19.1 (2026-10-23): the lines' sale and purchase wordings come from the
 * shared LineWordings (Search Customer Orders' expanded lines show the same).
 *
 * 1.15.0 (2026-10-15, Cam): "Refused — redeliver…" / "Edit redelivery…" in
 * the footer (RedeliverOrderModal, utils/redelivery), the Redelivery pill in
 * the header, and the package icon reads "Held at …" / "Redelivery …" while a
 * refused delivery waits to go again (it cannot be invoiced until then).
 *
 * 1.14.0 (2026-10-09, Cam): Edit is offered on a delivered or invoiced order
 * too — corrections only (utils/afterDelivery); the title says what stays.
 * "Update invoice from order…" offers an invoiced order's invoice the words,
 * item codes and supplier reference it still differs from the order on
 * (UpdateInvoiceFromOrderDialog).
 *
 * 1.13.0 (2026-10-08, Cam): Update Delivery is no longer refused because the
 * order is invoiced — until the invoice has gone out it may change (the
 * window says when it has, and offers the invoice changes). An invoiced
 * order offers "Update invoice from supplied qty…" (the changes its invoice
 * still needs, when the edit at the save was refused).
 *
 * 1.16.0 (2026-10-20, Cam): each line's quantity and supplied quantity shows
 * in its own unit with the tonnes it comes to beneath (QtyWithTonnes — "40 ×
 * 25kg bag" over "1 pallet · 1.00 T"), and the total row gives the tonnes
 * ordered and supplied. Display only.
 *
 * 1.17.0 (2026-10-21, Cam): each line always shows the item name, its sales
 * description (sell) and its purchase description (shopping_cart) — the
 * icons of Create Customer Order's lines — the purchase one being the PO-D
 * line's own words where the line is on a PO (po_line_description), else the
 * line's purchase text.
 * 1.18.0 (2026-10-22, Cam): a third-party order from our stock shows
 * "Collected" beside its delivery date — the day the haulier collected it,
 * blank until recorded — and its footer's "Collection / Delivery" opens
 * before the delivery date (utils/suppliedQty collectsFromStock). The package
 * icon reads Despatched (collected, before the delivery date).
 * 1.19.0 (2026-10-23, Cam): "Add to another order…" / "Added tonnage…" in the
 * footer (AddToOrderModal) — a split order's tonnage invoiced on the
 * customer's other order that day — and its pill in the header, which opens
 * the other order here. A split order whose tonnage went is not invoiced on
 * its own (Create Invoice greyed with the reason).
 */

import { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import { haulageServiceLabel } from '../utils/deliveryTerms';
import './CustomerOrderModal.css';
import PriceWithUnit from './PriceWithUnit';
import { formatPrice } from '../utils/currencyFormatter';
import { suppliedState } from '../utils/unitPriceLabel';
import LoadingSpinner from './LoadingSpinner';
import NonInventoryBadge from './NonInventoryBadge';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import SendToSupplierFlow from './SendToSupplierFlow';
import AuditHistoryModal from './AuditHistoryModal';
import { invoiceBlockFor, startInvoiceFromOrder } from '../utils/invoiceFromOrder';
import { SearchRevealContext } from '../hooks/useOpenInSearch';
import AddSuppliedQuantityModal from './AddSuppliedQuantityModal';
import SplitOrderModal from './SplitOrderModal';
import PurchaseOrderModal from './PurchaseOrderModal';
import LinkedDocuments from './LinkedDocuments';
import { formatVatWithRate } from '../utils/taxTypes';
import QtyWithTonnes from './QtyWithTonnes';
import { quantityWithTonnesText, qtyFactsOf, tonnesTotal } from '../utils/qtyWithTonnes';
import { useSlideOutButton } from './SlideOutButton';
import UpdateInvoiceFromSuppliedDialog from './UpdateInvoiceFromSuppliedDialog';
import UpdateInvoiceFromOrderDialog from './UpdateInvoiceFromOrderDialog';
import CustomIcon from './CustomIcon';
import useOpenInSearch from '../hooks/useOpenInSearch';
import { suppliedQtyBlock, collectsFromStock } from '../utils/suppliedQty';
import DivertOrderFlow from './DivertOrderFlow';
import DivertedBadge from './DivertedBadge';
import RedeliveryBadge from './RedeliveryBadge';
import RedeliverOrderModal from './RedeliverOrderModal';
import { redeliverActionFor } from '../utils/redeliveryActions';
import AddToOrderModal from './AddToOrderModal';
import TonnageAdditionBadge from './TonnageAdditionBadge';
import BookedOverBadge from './BookedOverBadge';
import { addToOrderActionFor } from '../utils/tonnageAdditionActions';
import { ADD_TO_ORDER_LABEL, ADDED_TONNAGE_LABEL } from '../utils/tonnageAdditions';
import { formatTonnes } from '../utils/numberFormatters';
import { redeliveryDeliveryStatus, redeliveryPillText } from '../utils/redelivery';
import SplitNotesModal from './SplitNotesModal';
import { divertBlockForOrder, splitBlockForOrder } from '../utils/orderHub';
import { orderEditTitle } from '../utils/afterDelivery';
import { billedIconForOrder, invoicedIconForOrder } from '../utils/orderStatusIcons';
import LineWordings from './LineWordings';
import useRepMobile from '../hooks/useRepMobile';
import RepLineBlock, { RepLineBlocks } from './RepLineBlock';
import { repOrderStatus, repPriceState, repDeliveryRoute, perUnitText } from '../utils/repOrderCard';
import { useAccess } from '../hooks/useAccess';
import RepChangeRequestModal from './RepChangeRequestModal';
import { orderAmendBlock } from '../utils/repOrderRequests';

/** A line with its item's sales account as `account_code`: a bulk line with no weight counts in tonnes. */
const lineFacts = (line) => (line && !line.account_code && line.item_sales_account_code
  ? { ...line, account_code: line.item_sales_account_code } : line);

function CustomerOrderModal({ orderId, user, onClose, onEdit, onOpenInSearch, onOpenOrderInPOSearch, onOpenPOInSearch }) {
  // A rep's phone: status in words and the lines as blocks (1.20.0).
  const repMobile = useRepMobile();
  // A rep asks the office for a change (rep order requests, 30 Oct 2026).
  const { badge: viewerBadge, canWrite: viewerCanWrite } = useAccess();
  const canRequestChange = viewerBadge === 'REP' && viewerCanWrite('rep_order_requests.send');
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  // "Find in …" wherever this was opened from (hooks/useOpenInSearch).
  const { open: openInSearch } = useOpenInSearch('customer_order', onOpenInSearch, onClose);
  // The order whose purchase orders are being sent on.
  const [sendingOrder, setSendingOrder] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(orderId);
  const [viewingPOId, setViewingPOId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [showHistory, setShowHistory] = useState(false);
  // Create Invoice is a screen, not a modal: this window closes and the app
  // navigates there (the Dashboard's navigation, via the search-reveal context).
  const appNav = useContext(SearchRevealContext)?.onNavigate;
  const [showSuppliedQty, setShowSuppliedQty] = useState(false);
  const [showInvoiceUpdate, setShowInvoiceUpdate] = useState(false);
  // "Update invoice from order…" (corrections after invoicing, 2026-10-09).
  const [showInvoiceFromOrder, setShowInvoiceFromOrder] = useState(false);
  const [showSplitOrder, setShowSplitOrder] = useState(false);
  // Divert: the whole order to another customer (DivertOrderFlow).
  const [showDivert, setShowDivert] = useState(false);
  // Refused — redeliver… / Edit redelivery… (RedeliverOrderModal).
  const [showRedeliver, setShowRedeliver] = useState(false);
  // Add to another order… / Added tonnage… (AddToOrderModal).
  const [showAddToOrder, setShowAddToOrder] = useState(false);
  // Add or change the reason for a split, afterwards.
  const [editingSplitNotes, setEditingSplitNotes] = useState(false);
  const [linkedOrders, setLinkedOrders] = useState([]);

  // Slideout buttons — re-measure when order loads or sub-modals open
  useSlideOutButton([order, showHistory, showSuppliedQty, showSplitOrder, showDivert, showRedeliver, showAddToOrder]);

  // Sync internal state if prop changes externally
  useEffect(() => { setCurrentOrderId(orderId); }, [orderId]);

  useEffect(() => {
    if (currentOrderId) {
      setOrder(null);
      setLoading(true);
      setLinkedOrders([]);
      fetchOrderDetails();
      // Prevent background scrolling
      document.body.classList.add('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.add('modal-open');
      }
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/${currentOrderId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setOrder(data);

      // Fetch linked orders (parent + siblings/children)
      if (data.parent_order_id || data.id) {
        try {
          const parentId = data.parent_order_id || data.id;
          const linkedRes = await fetch(`${API_URL}/api/orders/customer-orders/search?search=${encodeURIComponent(data.order_number?.replace(/[A-Z]$/, ''))}&limit=20`, {
            credentials: 'include'
          });
          const linkedData = await linkedRes.json();
          if (linkedData.success) {
            // Filter to orders that share the same base order number (parent + children)
            const baseNumber = data.order_number?.replace(/[A-Z]$/, '');
            const linked = (linkedData.orders || []).filter(o =>
              o.id !== data.id && (
                o.order_number === baseNumber ||
                o.order_number?.startsWith(baseNumber) ||
                o.parent_order_id === data.id ||
                o.parent_order_id === data.parent_order_id
              )
            );
            setLinkedOrders(linked);
          }
        } catch (err) {
          console.error('Failed to fetch linked orders:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
      onClose();
    }, 300);
  };

  // Status icon colors
  const muted = 'rgba(var(--text-muted-rgb), 0.5)';
  const primary = 'var(--text-primary)';
  const error = 'var(--color-error)';
  const warning = 'var(--color-warning)';
  const success = 'var(--color-success)';

  const getStatusIcons = (o) => {
    const po = o.po_status || 'No POs Linked';
    const sent = o.sent_status || 'No POs Sent';
    const confirmed = o.confirmed_status || 'No POs Confirmed';
    const transport = o.transport_status || 'Not Planned';
    const delivery = o.delivery_status || 'Not Despatched';
    const invoiced = o.invoiced_status || 'Not Invoiced';

    const cartMap = { 'No POs Linked': { color: error, title: 'No POs Linked' }, 'POs Partially Linked': { color: warning, title: 'POs Partially Linked' }, 'POs Linked': { color: success, title: 'POs Linked' } };
    const cart = cartMap[po] || cartMap['No POs Linked'];

    let mail;
    if (po === 'No POs Linked') mail = { color: muted, title: 'Not Sent' };
    else if (sent === 'No POs Sent') mail = { color: error, title: 'No POs Sent' };
    else if (sent === 'POs Partially Sent') mail = { color: warning, title: 'POs Partially Sent' };
    else mail = { color: success, title: 'POs Sent' };

    let confirm;
    if (sent === 'No POs Sent') confirm = { color: muted, title: 'Not Confirmed' };
    else if (confirmed === 'No POs Confirmed') confirm = { color: error, title: 'No POs Confirmed' };
    else if (confirmed === 'POs Partially Confirmed') confirm = { color: warning, title: 'POs Partially Confirmed' };
    else confirm = { color: success, title: 'POs Confirmed' };

    // Truck — muted only when no POs linked; error when POs exist but transport not planned
    let truck;
    if (po === 'No POs Linked') { truck = { color: muted, title: 'Not Planned' }; }
    else if (transport === 'Not Planned') { truck = { color: error, title: 'Not Planned' }; }
    else if (transport === 'Partially Planned') { truck = { color: warning, title: 'Partially Planned' }; }
    else { truck = { color: success, title: 'Transport Planned' }; }

    const pkgMap = { 'Not Despatched': { color: muted, title: 'Not Despatched' }, 'Despatched': { color: primary, title: o.collected_date ? `Despatched — collected ${formatDate(o.collected_date)}` : 'Despatched' }, 'Partially Despatched': { color: warning, title: 'Partially Despatched' }, 'In-Transit': { color: primary, title: 'In-Transit' }, 'Partially Delivered': { color: warning, title: 'Partially Delivered' }, 'Delivered': { color: success, title: 'Delivered' } };
    // Refused on delivery and waiting to go again (utils/redelivery).
    const pkg = redeliveryDeliveryStatus(o.redelivery)
      ? { color: warning, title: redeliveryPillText(o.redelivery) }
      : (pkgMap[delivery] || pkgMap['Not Despatched']);

    // Billed — calculated from purchase_orders array
    let billed;
    const pos = o.purchase_orders || [];
    const poCount = pos.length;
    const billedCount = pos.filter(p => p.status?.toLowerCase() === 'billed').length;
    // The server's billed_status (2026-10-14) is the one rule Search Customer
    // Orders' icon and Status filter read; the PO list is the fallback.
    if (o.billed_status) billed = billedIconForOrder(o);
    else if (po === 'No POs Linked') billed = { color: muted, title: 'Not Billed' };
    else if (poCount > 0 && billedCount === 0) billed = { color: error, title: 'Not Billed' };
    else if (billedCount >= poCount) billed = { color: success, title: 'POs Billed' };
    else if (billedCount > 0) billed = { color: warning, title: `${billedCount} of ${poCount} POs Billed` };
    else billed = { color: muted, title: 'Not Billed' };

    // As the search row reads it — a draft invoice shown apart (not invoiced).
    const receipt = invoicedIconForOrder({ ...o, delivery_status: delivery, invoiced_status: invoiced });

    const pricedColor = o.is_priced === 'Priced' ? success : o.is_priced === 'Partially Priced' ? warning : error;
    const pricedTitle = o.is_priced || 'Not Priced';

    return { cart, mail, confirm, truck, pkg, billed, receipt, pricedColor, pricedTitle };
  };

  const getCreditStatusDisplay = (status) => {
    switch (status) {
      case 'no_limit':
        return 'No Limit Set';
      case 'under_limit':
        return 'Under Limit';
      case 'over_limit':
        return 'Over Limit';
      default:
        return 'Unknown';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const parseAddressWithLineBreaks = (addressString) => {
    if (!addressString) return 'No address on file';

    // If it's an object, format it
    if (typeof addressString === 'object') {
      const parts = [
        addressString.addressLine1,
        addressString.addressLine2,
        addressString.addressLine3,
        addressString.city,
        addressString.region,
        addressString.postalCode
      ].filter(part => part && part.trim());

      return parts.map((part, i, arr) => (
        <span key={i}>
          {part}{i < arr.length - 1 && <br />}
        </span>
      ));
    }

    // If it's a JSON string, parse it
    if (typeof addressString === 'string' && addressString.startsWith('{')) {
      try {
        const addressObj = JSON.parse(addressString);
        const parts = [
          addressObj.addressLine1,
          addressObj.addressLine2,
          addressObj.addressLine3,
          addressObj.city,
          addressObj.region,
          addressObj.postalCode
        ].filter(part => part && part.trim());

        return parts.map((part, i, arr) => (
          <span key={i}>
            {part}{i < arr.length - 1 && <br />}
          </span>
        ));
      } catch (e) {
        return addressString;
      }
    }

    return addressString;
  };

  // Open the PO view modal in place. PurchaseOrderModal fetches by the Xero PO
  // id (string, incl. TEMP-/LOCAL- placeholders), not the local DB id — the line
  // carries that as xero_purchase_order_id from the order-detail endpoint.
  const handleViewPO = (line) => {
    if (line?.xero_purchase_order_id) {
      setViewingPOId(line.xero_purchase_order_id);
    } else {
      setAlertDialog({
        isOpen: true,
        message: `Unable to open PO ${line?.purchase_order_number || ''}: no Xero reference on file.`,
        type: 'warning'
      });
    }
  };

  // Editable at any stage since 2026-10-09: after delivery or invoicing, for
  // corrections (the server refuses what may no longer change).
  const canEdit = !!order;

  const hasPurchaseOrders = order?.purchase_orders && order.purchase_orders.length > 0;

  if (!currentOrderId) return null;

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content customer-order-modal ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-left">
            <h2>{loading ? 'Loading...' : `Order ${order?.order_number}`}</h2>
            {/* Derived from the lines actually on screen rather than a flag from
                the list query, so the modal is right even when opened directly. */}
            {!loading && Array.isArray(order?.lines) && (
              <NonInventoryBadge
                present={order.lines.some(l => !l.inventory_item_id)}
                count={order.lines.filter(l => !l.inventory_item_id).length}
              />
            )}
            {/* Sent to another customer since it was raised — from → to, when,
                who and why on hover; a click lists them, reason editable. */}
            {!loading && order && (
              <DivertedBadge diversions={order.diversions} orderId={order.id} orderNumber={order.order_number}
                onChanged={fetchOrderDetails} />
            )}
            {/* Refused on delivery: held, to go again, or redelivered. */}
            {!loading && order && (
              <RedeliveryBadge redelivery={order.redelivery} orderId={order.id} onChanged={fetchOrderDetails} />
            )}
            {/* A split's tonnage invoiced on the customer's other order — a
                click opens that order here. */}
            {!loading && order && (
              <TonnageAdditionBadge additions={order.tonnage_additions} orderId={order.id}
                onOpenOrder={(id) => setCurrentOrderId(id)} onChanged={fetchOrderDetails} />
            )}
            {/* Its invoice booked over (moved to another date, or split) — each
                part opens its invoice (BookedOverBadge). */}
            {!loading && order && (
              <BookedOverBadge parts={order.booked_over} onChanged={fetchOrderDetails} />
            )}
            {!loading && order && repMobile && (() => {
              const status = repOrderStatus(order);
              const price = repPriceState(order);
              const route = repDeliveryRoute(order);
              return (
                <div className="rep-order-status-words">
                  <span className={`roc-chip tone-${status.tone}`}>{status.label}</span>
                  {price && <span className={`roc-chip tone-${price.tone}`}>{price.label}</span>}
                  {route && <span className="roc-chip tone-muted">{route}</span>}
                </div>
              );
            })()}
            {!loading && order && !repMobile && (() => {
              const icons = getStatusIcons(order);
              return (
                <div className="status-icons">
                  <span className="material-symbols-rounded" style={{ color: icons.cart.color }} title={icons.cart.title}>shopping_cart</span>
                  <span className="material-symbols-rounded" style={{ color: icons.mail.color }} title={icons.mail.title}>mail</span>
                  <span className="material-symbols-rounded" style={{ color: icons.confirm.color }} title={icons.confirm.title}>check_circle</span>
                  <span className="material-symbols-rounded" style={{ color: icons.truck.color }} title={icons.truck.title}>local_shipping</span>
                  <span className="material-symbols-rounded" style={{ color: icons.pkg.color }} title={icons.pkg.title}>package_2</span>
                  <span className="material-symbols-rounded" style={{ color: icons.billed.color }} title={icons.billed.title}>receipt_long</span>
                  <span className="material-symbols-rounded" style={{ color: icons.receipt.color }} title={icons.receipt.title}>receipt</span>
                  <span className="material-symbols-rounded" style={{ color: icons.pricedColor }} title={icons.pricedTitle}>verified</span>
                </div>
              );
            })()}
          </div>
          <div className="header-actions">
            {onEdit && !loading && canEdit && (
              <button
                className="btn-icon"
                onClick={() => {
                  handleClose();
                  onEdit(order.id);
                }}
                title={orderEditTitle(order)}
              >
                <span className="material-symbols-rounded">edit</span>
              </button>
            )}
            <button
              className="btn-icon"
              onClick={() => setShowHistory(true)}
              disabled={loading}
              title="View change history"
            >
              <span className="material-symbols-rounded">history</span>
            </button>
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="modal-body">
            <LoadingSpinner />
          </div>
        ) : order ? (
          <>
          <div className="modal-body">
            {/* Customer Details Section */}
            {order.customer_details && (
              <div className="customer-info-section">
                <h3>Customer Details</h3>
                <div className="customer-info-grid">
                  <div className="info-left">
                    <div className="info-row">
                      <div className="info-item">
                        <span className="info-label">Account #</span>
                        <span className={`info-value acc_no ${order.customer_details.account_no ? '' : 'not_set'}`}>{order.customer_details.account_no || 'Not set'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Customer Name</span>
                        <span className="info-value">{order.customer_details.company_name}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Credit Status</span>
                        <span className={`credit-status ${order.customer_details.credit_status?.replace('_', '-')}`}>
                          {getCreditStatusDisplay(order.customer_details.credit_status)}
                        </span>
                      </div>
                    </div>
                    <div className="info-row">
                      <div className="info-item">
                        <span className="info-label">Phone</span>
                        <span className="info-value">{order.customer_details.phone || order.customer_details.mobile || 'No phone'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Email</span>
                        <span className="info-value">{order.customer_details.email || 'No email'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Rep</span>
                        <span className="info-value">{order.customer_details.sales_rep || 'Not assigned'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="info-right">
                    <div className="info-row">
                      <div className="info-item">
                        <span className="info-label">
                          Delivery Address
                          {order.customer_details.address_type === 'invoice' && (
                            <span className="address-type-badge">INV</span>
                          )}
                        </span>
                        <span className="info-value">{parseAddressWithLineBreaks(order.customer_details.delivery_address || order.customer_details.address)}</span>
                      </div>
                      <div className="info-item notes">
                        <span className="info-label highlight">Customer Notes</span>
                        <span className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
                          {order.customer_details.notes || 'No notes'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Order Details Section */}
            <div className="order-main-sections">
              <div className="order-details-section">
                <h3>Order Details</h3>
                <div className="order-details-grid">
                  <div className="detail-row">
                    <span className="label">Order Date:</span>
                    <span className="value">{formatDate(order.order_date)}</span>
                  </div>
                  {order.delivery_date && (
                    <div className="detail-row">
                      <span className="label">Delivery Date:</span>
                      <span className="value delivery-date-value">{formatDate(order.delivery_date)}</span>
                    </div>
                  )}
                  {/* Third-party from our stock: the day the haulier collected
                      it, blank until recorded (Collection / Delivery). */}
                  {(order.collected_date || collectsFromStock(order)) && (
                    <div className="detail-row">
                      <span className="label">Collected:</span>
                      <span className={`value collected-date-value${order.collected_date ? '' : ' muted'}`}>
                        {order.collected_date ? formatDate(order.collected_date) : 'Not yet — record it with Collection / Delivery'}
                      </span>
                    </div>
                  )}
                  {Array.isArray(order.option_dates) && order.option_dates.length > 0 && (
                    <div className="detail-row">
                      <span className="label">Option Dates:</span>
                      <span className="value option-dates-value">
                        {order.option_dates.map((o, i) => (
                          <span key={i} className="option-date-chip">
                            {formatDate(o.date)}
                            <span className="option-date-pref-badge">P{o.preference}</span>
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                  {order.supplier_delivery_terms && (
                    <div className="detail-row">
                      <span className="label">Supplier Delivery Terms:</span>
                      <span className="value" style={{fontWeight: '600'}}>{order.supplier_delivery_terms}</span>
                    </div>
                  )}
                  {order.excel_agri_delivery_terms && (
                    <div className="detail-row">
                      <span className="label">Excel Agri Delivery Terms:</span>
                      <span className="value">{order.excel_agri_delivery_terms}</span>
                    </div>
                  )}
                  {/* Third-party haulage: the haulier and its PO-H. The PO-H
                      is not among the order's purchase orders — it buys
                      nothing — so it is listed here on its own. */}
                  {(order.haulier_name || (order.haulage_pos || []).length > 0) && (
                    <div className="detail-row">
                      <span className="label">Third-Party Haulier:</span>
                      <span className="value">
                        {order.haulier_name || '—'}
                        {(order.haulage_pos || []).map(po => (
                          <button
                            key={po.id}
                            type="button"
                            className="linked-order-link"
                            style={{ marginLeft: 'var(--spacing-sm)' }}
                            title={`Open ${po.purchase_order_number}${po.sent_to_contact ? '' : ' — not yet sent'}`}
                            onClick={() => setViewingPOId(po.xero_purchase_order_id)}
                          >
                            {po.purchase_order_number}
                          </button>
                        ))}
                        {!(order.haulage_pos || []).length && <span> · no PO-H yet</span>}
                      </span>
                    </div>
                  )}
                  {order.haulage_service && (
                    <div className="detail-row">
                      <span className="label">Delivery Type:</span>
                      <span className="value">
                        {haulageServiceLabel(order.haulage_service)}
                        {order.haulage_service === 'economy' && <span> · no fixed date</span>}
                      </span>
                    </div>
                  )}
                  {/* What will physically get down the lane, ahead of what
                      to do when you arrive — the vehicle decides whether the
                      instructions are even relevant. From the contact record,
                      so it is the same figure the order screens show. */}
                  {order.customer_largest_vehicle_access?.trim() && (
                    <div className="detail-row">
                      <span className="label">Largest Vehicle Access:</span>
                      <span className="value lva-value">
                        <span className="material-symbols-rounded">local_shipping</span>
                        {order.customer_largest_vehicle_access.trim()}
                      </span>
                    </div>
                  )}
                  {/* These are the order's delivery instructions — the driver's
                      instructions — and calling them Order Notes had people
                      looking for them among the customer's notes instead. */}
                  {order.notes && (
                    <div className="detail-row">
                      <span className="label">Delivery Instructions:</span>
                      <span className="value">{order.notes}</span>
                    </div>
                  )}
                  {/* The reason for a split, on a split order or its parent —
                      added or changed afterwards (SplitNotesModal). */}
                  {(order.parent_order_id || order.has_splits) && (
                    <div className="detail-row full-width">
                      <span className="label">Split Notes:</span>
                      <span className="value">
                        {(order.parent_order_id ? order.split_notes : order.child_split_notes) || 'None recorded'}
                        <button
                          type="button"
                          data-write-action
                          className="btn-icon split-notes-edit"
                          title="Add or change the reason for the split"
                          onClick={() => setEditingSplitNotes(true)}
                        >
                          <span className="material-symbols-rounded">edit</span>
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Its invoice(s) once invoiced, its POs (PO-D and PO-H), the bills
                on them once billed, and credits allocated to its invoices —
                each opens that document's own modal on top of this one. */}
            <LinkedDocuments type="customer_order" id={order.id} refreshKey={order} onChange={fetchOrderDetails} />

            <div className="order-lines-section">
              <h3>Order Lines</h3>
              {repMobile ? (
                <RepLineBlocks empty="No order lines">
                  {(order.lines || []).map((line, index) => {
                    const sale = Number(line.unit_price);
                    const ordered = line.delivered_quantity && parseFloat(line.delivered_quantity) !== parseFloat(line.quantity)
                      ? line.delivered_quantity : line.quantity;
                    return (
                      <RepLineBlock
                        key={line.id || index}
                        code={line.product_code}
                        name={line.item_name || line.product_name}
                        words={[line.product_name, line.additive_name ? `+ ${line.additive_name}` : '', line.supplier_name ? `From ${line.supplier_name}` : '']}
                        facts={[
                          { key: 'ordered', label: 'Ordered', value: <QtyWithTonnes quantity={ordered} line={line} /> },
                          { key: 'supplied', label: 'Supplied', value: line.supplied_quantity != null ? <QtyWithTonnes quantity={line.supplied_quantity} line={line} pallets={null} /> : null, none: 'Not yet' },
                          {
                            key: 'price',
                            label: 'Price',
                            align: 'right',
                            value: sale > 0 ? (
                              <span className="roc-fact-value">
                                {formatPrice(sale)}
                                <span className="roc-fact-per"> {perUnitText(line)}</span>
                              </span>
                            ) : null,
                            none: 'Not priced'
                          }
                        ]}
                      >
                        <div className="rep-line-foot">
                          <span>VAT {formatVatWithRate(line.tax_amount, line.tax_type, formatCurrency)}</span>
                          <span>Net <strong>{formatCurrency(line.line_total)}</strong></span>
                        </div>
                        {line.purchase_order_number ? (
                          <button
                            type="button"
                            data-read-safe
                            className="roc-line-po"
                            onClick={() => handleViewPO(line)}
                            aria-label={`Open ${line.purchase_order_number}`}
                          >
                            <span className="material-symbols-rounded" aria-hidden="true">shopping_cart</span>
                            <span className="roc-line-po-text">{line.purchase_order_number}</span>
                            <span className="material-symbols-rounded roc-chevron" aria-hidden="true">chevron_right</span>
                          </button>
                        ) : null}
                      </RepLineBlock>
                    );
                  })}
                  {order.lines && order.lines.length > 0 && (
                    <div className="roc-line rep-line-total">
                      <div className="rep-line-foot">
                        <span>Total</span>
                        <strong>{formatCurrency(order.total_amount)}</strong>
                      </div>
                    </div>
                  )}
                </RepLineBlocks>
              ) : (
              <div className="table-container">
                <table className="order-lines-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Supplier</th>
                      {hasPurchaseOrders && <th style={{ textAlign: 'center' }}>PO #</th>}
                      <th style={{ textAlign: 'right' }}>Quantity</th>
                      <th style={{ textAlign: 'right' }}>Supplied</th>
                      <th style={{ textAlign: 'right' }}>Unit £</th>
                      <th style={{ textAlign: 'right' }}>VAT</th>
                      <th style={{ textAlign: 'right' }}>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines && order.lines.length > 0 ? (
                      order.lines.map((line, index) => (
                        <tr key={index}>
                          <td>
                            <div className="product-cell">
                              {line.product_code && (
                                <span className="product-code">{line.product_code}</span>
                              )}
                              <div className="product-name-row">
                                {/* The CATALOGUE name, resolved from the item —
                                    order_lines.product_name is not the item's
                                    name, it is the line's editable SALE
                                    description. Name here, the sale and purchase
                                    wordings underneath, so none is lost. */}
                                <span className="product-name">{line.item_name || line.product_name}</span>
                                {line.additive_name && (
                                  <div className="additive-info">+ {line.additive_name}</div>
                                )}
                                <span
                                  className={`source-icon ${line.source || 'inventory'}`}
                                  title={line.source === 'contract' ? 'Contract pricing' : 'Inventory pricing'}
                                >
                                  {line.source === 'contract' ? (
                                    <span className="material-symbols-rounded">handshake</span>
                                  ) : (
                                    <span className="material-symbols-rounded">category</span>
                                  )}
                                </span>
                              </div>
                              {/* The line's two wordings, always (Cam, 21 Oct
                                  2026 — item name, sales description and
                                  purchase description, with Create Customer
                                  Order's icons): Sale (product_name — the
                                  invoice) and Purchase — the PO-D line's own
                                  words where the line is on a PO
                                  (po_line_description), else the line's
                                  purchase text (purchase_text — stored, or
                                  derived for an older one;
                                  services/orderLinePurchaseText). Both are the
                                  order line's own: the PO is never looked up by
                                  item code, which picked the wrong line when
                                  two shared a code. */}
                              <LineWordings
                                sale={line.product_name}
                                purchase={line.po_line_description || line.purchase_text}
                                purchaseTitle={line.po_line_description && line.purchase_order_number
                                  ? `Purchase description: the words on ${line.purchase_order_number}, and on the supplier's bill`
                                  : undefined}
                              />
                            </div>
                          </td>
                          <td>{line.supplier_name || '-'}</td>
                          {hasPurchaseOrders && (
                            <td style={{ textAlign: 'center' }}>
                              {line.purchase_order_number ? (
                                <button
                                  className="po-number-badge clickable"
                                  onClick={() => handleViewPO(line)}
                                  title="Click to view this Purchase Order"
                                >
                                  {line.purchase_order_number}
                                </button>
                              ) : '-'}
                            </td>
                          )}
                          {/* The quantity in the line's own unit and the
                              tonnes it comes to (utils/qtyWithTonnes — Cam,
                              20 Oct 2026), ordered and supplied alike, so the
                              two can be compared at a glance. */}
                          <td className="quantity">
                            {(() => {
                              const delivered = line.delivered_quantity && parseFloat(line.delivered_quantity) !== parseFloat(line.quantity);
                              return (
                                <>
                                  <QtyWithTonnes quantity={line.delivered_quantity || line.quantity} line={lineFacts(line)}
                                    pallets={delivered ? null : line.pallets} />
                                  {delivered && (
                                    <span className="delivered-qty-badge" title={`Ordered: ${quantityWithTonnesText(line.quantity, qtyFactsOf(line))}`}> (d)</span>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td className="quantity supplied-qty">
                            {line.supplied_quantity != null
                              ? <QtyWithTonnes quantity={line.supplied_quantity} line={lineFacts(line)} pallets={null} />
                              : (() => {
                                const st = suppliedState(null, { invoiced: order.invoiced_status === 'Invoiced' });
                                return <span className={`not-set supplied-not-recorded${order.invoiced_status === 'Invoiced' ? '' : ' needed'}`} title={st.title}>{st.text}</span>;
                              })()
                            }
                            {/* A split order's tonnage invoiced on this line (utils/tonnageAdditions). */}
                            {(order.tonnage_additions?.includes || []).filter(a => Number(a.target_order_line_id) === Number(line.id)).map(a => (
                              <span key={a.id} className="tonnage-line-added" title={`Invoiced on this line with ${a.source_order_number}'s tonnage`}>
                                +{formatTonnes(a.tonnes)} from {a.source_order_number}
                              </span>
                            ))}
                          </td>
                          <td className="currency"><PriceWithUnit value={line.unit_price || 0} line={line} /></td>
                          <td className="tax-type text-right">{formatVatWithRate(line.tax_amount, line.tax_type, formatCurrency)}</td>
                          <td className="currency total">{formatCurrency(line.line_total)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={hasPurchaseOrders ? "9" : "8"} className="no-lines">No order lines</td>
                      </tr>
                    )}
                  </tbody>
                  {order.lines && order.lines.length > 0 && (
                    <tfoot>
                      <tr className="total-row">
                        <td colSpan={hasPurchaseOrders ? "3" : "2"}>Total</td>
                        {/* What the lines come to in tonnes, ordered and
                            supplied (lines of unknown weight left out, and
                            said so in the cell's title). */}
                        {(() => {
                          const ordered = tonnesTotal(order.lines.map(l => ({ ...qtyFactsOf(l), quantity: l.delivered_quantity || l.quantity, pallets: null })));
                          const supplied = tonnesTotal(order.lines.filter(l => l.supplied_quantity != null).map(l => ({ ...qtyFactsOf(l), quantity: l.supplied_quantity, pallets: null })));
                          return (
                            <>
                              <td className="quantity" title={ordered.title || undefined}>{ordered.text}</td>
                              <td className="quantity supplied-qty" title={supplied.title || undefined}>{supplied.text}</td>
                            </>
                          );
                        })()}
                        <td colSpan="2"></td>
                        <td className="currency total">{formatCurrency(order.total_amount)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              )}
            </div>

            {/* Linked Orders Section */}
            {linkedOrders.length > 0 && (
              <div className="linked-orders-section">
                <h3>
                  <span className="material-symbols-rounded">link</span>
                  Linked Orders
                </h3>
                <div className="linked-orders-list">
                  {order.parent_order_id && (
                    <div className="linked-order-item parent">
                      <span className="linked-label">Parent</span>
                      {linkedOrders
                        .filter(o => o.id === order.parent_order_id)
                        .map(o => (
                          <button
                            key={o.id}
                            className="linked-order-link"
                            onClick={() => setCurrentOrderId(o.id)}
                          >
                            {o.order_number}
                            <span className="linked-customer">{o.company_name}</span>
                          </button>
                        ))
                      }
                    </div>
                  )}
                  {linkedOrders.filter(o => o.id !== order.parent_order_id).length > 0 && (
                    <div className="linked-order-item children">
                      <span className="linked-label">{order.parent_order_id ? 'Siblings' : 'Split Orders'}</span>
                      <div className="linked-order-chips">
                        {linkedOrders
                          .filter(o => o.id !== order.parent_order_id)
                          .map(o => (
                            <div key={o.id} className="linked-order-chip-row">
                              <button
                                className="linked-order-link"
                                onClick={() => setCurrentOrderId(o.id)}
                              >
                                {o.order_number}
                                <span className="linked-customer">{o.company_name}</span>
                              </button>
                              {/* Show delete button for child splits (not invoiced) when viewing parent */}
                              {!order.parent_order_id && o.status?.toLowerCase() !== 'invoiced' && (
                                <button
                                  className="btn-icon btn-danger linked-delete"
                                  title={`Delete ${o.order_number} (restores qty to this order)`}
                                  onClick={() => {
                                    setConfirmDialog({
                                      isOpen: true,
                                      message: `Delete split order ${o.order_number}? Its quantities will be restored to this order.`,
                                      onConfirm: async () => {
                                        try {
                                          const res = await fetch(`${API_URL}/api/orders/customer-orders/${o.id}`, {
                                            method: 'DELETE',
                                            credentials: 'include'
                                          });
                                          const data = await res.json();
                                          if (data.success) {
                                            setAlertDialog({ isOpen: true, message: data.message, type: 'success' });
                                            setLoading(true);
                                            fetchOrderDetails();
                                          } else {
                                            throw new Error(data.error);
                                          }
                                        } catch (err) {
                                          setAlertDialog({ isOpen: true, message: `Failed to delete: ${err.message}`, type: 'error' });
                                        }
                                      }
                                    });
                                  }}
                                >
                                  <span className="material-symbols-rounded">delete</span>
                                </button>
                              )}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* Footer with action buttons */}
          {(() => {
            const deliveryStatus = order.delivery_status || 'Not Despatched';
            const isThirdParty = order.excel_agri_delivery_terms === 'Third-Party Delivery';
            const isInvoiced = order.invoiced_status === 'Invoiced';
            // utils/suppliedQty: not before the delivery day, nor (where POs
            // drive it) before despatch; stock-only and third-party exempt.
            const collects = collectsFromStock(order);
            const supplyBlock = suppliedQtyBlock({
              deliveryDate: order.delivery_date,
              deliveryStatus,
              terms: order.excel_agri_delivery_terms,
              hasSupplyPos: (order.lines || []).some(l => l.purchase_order_id),
              collects
            });
            const canAddSuppliedQty = !supplyBlock;
            const allLinesHaveSupplied = order.lines?.length > 0 && order.lines.every(l => l.supplied_quantity != null);
            const isSplitChild = !!order.parent_order_id;
            const hasSplitChildren = linkedOrders.some(o => o.parent_order_id === order.id);
            // A draft invoice waiting in Price Orders bills the whole order (utils/orderHub).
            const canSplitOrder = canAddSuppliedQty && allLinesHaveSupplied && !isSplitChild && !hasSplitChildren && !order.draft_invoice_no;
            const invoiceBlock = appNav ? invoiceBlockFor({ ...order, delivery_status: deliveryStatus })
              : 'Open the order from a list to create its invoice';
            const canInvoice = !invoiceBlock;

            return (
              <div className="modal-footer">
                {/* A rep: ask the office to change this order (a request,
                    one of the two writes a rep may make). */}
                {canRequestChange && (() => {
                  const block = orderAmendBlock(order);
                  return (
                    <button
                      data-read-safe
                      className="btn-slideout"
                      onClick={() => { if (!block) setChangeRequestOpen(true); }}
                      disabled={!!block}
                      title={block || 'Ask the office to change this order'}
                    >
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">edit_note</span>
                      </div>
                      <span className="btn-label">Request a change</span>
                    </button>
                  );
                })()}
                {/* Back to the list this order lives in. Supplied by
                    EntityModalProvider, so it appears wherever the modal was
                    opened from and not only on Search Orders. */}
                {openInSearch && order.order_number && (
                  <button
                    data-read-safe
                    className="btn-slideout"
                    onClick={() => openInSearch(order)}
                    title={`Show ${order.order_number} in Search Orders`}
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">manage_search</span>
                    </div>
                    <span className="btn-label">Find in Search Orders</span>
                  </button>
                )}
                {/* Send the order's purchase orders on. One supplier goes
                    straight to the email modal; several hand over to Search
                    Purchase Orders, which is the only place they can be sent
                    one at a time. SendToSupplierFlow decides which. */}
                <button data-write-action
                  className="btn-slideout"
                  onClick={() => {
                    if (!hasPurchaseOrders) {
                      setAlertDialog({ isOpen: true, type: 'info', message: `No purchase orders have been raised for ${order.order_number} yet, so there is nothing to send.` });
                    } else {
                      setSendingOrder(order);
                    }
                  }}
                  title={hasPurchaseOrders ? 'Send to Supplier' : 'No purchase orders raised against this order'}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">forward_to_inbox</span>
                  </div>
                  <span className="btn-label">Send to Supplier</span>
                </button>
                {isThirdParty ? (
                  // Status and supplied quantities together, whatever the
                  // dates say — the haulier's delivery is not ours to see.
                  <button data-write-action
                    className={`btn-slideout ${supplyBlock ? 'disabled' : (deliveryStatus === 'Delivered' && allLinesHaveSupplied) ? 'supplied-set' : ''}`}
                    title={supplyBlock || (isInvoiced
                      ? 'Invoiced — it can still change until the invoice has gone out to the customer'
                      : collects ? 'Record the haulier\'s collection (date and quantities) — it is delivered by its date after that'
                        : 'Record delivery status and supplied quantities')}
                    onClick={() => {
                      if (supplyBlock) {
                        setAlertDialog({ isOpen: true, type: 'info', message: `Not yet: ${supplyBlock}.` });
                      } else {
                        setShowSuppliedQty(true);
                      }
                    }}
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">local_shipping</span>
                    </div>
                    <span className="btn-label">{collects ? 'Collection / Delivery' : 'Update Delivery'}</span>
                  </button>
                ) : (
                <button data-write-action
                  className={`btn-slideout ${!canAddSuppliedQty ? 'disabled' : allLinesHaveSupplied ? 'supplied-set' : ''}`}
                  onClick={() => {
                    if (!canAddSuppliedQty) {
                      setAlertDialog({ isOpen: true, message: `Supplied quantities cannot be recorded yet: ${supplyBlock}.`, type: 'info' });
                    } else {
                      setShowSuppliedQty(true);
                    }
                  }}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">inventory</span>
                  </div>
                  <span className="btn-label">{allLinesHaveSupplied ? 'Update Supplied Qty' : 'Add Supplied Qty'}</span>
                </button>
                )}
                <button data-write-action
                  className={`btn-slideout ${!canSplitOrder ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!canSplitOrder) {
                      const reason = order.draft_invoice_no
                        ? `${splitBlockForOrder(order)}.`
                        : isSplitChild
                        ? 'This is a child split order and cannot be split again. Delete the child order first to re-split the parent.'
                        : hasSplitChildren
                        ? 'This order already has split orders. Delete existing child orders first to re-split.'
                        : 'All order lines must have a supplied quantity set before splitting. Use "Add Supplied Qty" first.';
                      setAlertDialog({ isOpen: true, message: reason, type: 'info' });
                    } else {
                      setShowSplitOrder(true);
                    }
                  }}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">call_split</span>
                  </div>
                  <span className="btn-label">Split Order</span>
                </button>
                {/* Divert: the WHOLE order to another customer, numbers and
                    links kept — disabled with the reason once invoiced. */}
                {(() => {
                  const divertBlock = divertBlockForOrder(order);
                  return (
                    <button data-write-action
                      className={`btn-slideout ${divertBlock ? 'disabled' : ''}`}
                      title={divertBlock || 'Divert — send the whole order to another customer'}
                      onClick={() => {
                        if (divertBlock) setAlertDialog({ isOpen: true, message: divertBlock, type: 'info' });
                        else setShowDivert(true);
                      }}
                    >
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">alt_route</span>
                      </div>
                      <span className="btn-label">Divert…</span>
                    </button>
                  );
                })()}
                {/* Refused on delivery: where it is held and when it goes
                    again — the order and its PO-D stay as they are. */}
                {(() => {
                  const { mode, block } = redeliverActionFor({
                    ...order, has_supply_pos: (order.lines || []).some(l => l.purchase_order_id)
                  }, user);
                  return (
                    <button data-write-action
                      className={`btn-slideout ${block ? 'disabled' : ''}`}
                      title={block || (mode === 'edit' ? 'Change the redelivery — its date, where it is held, or cancel it' : 'The customer refused it — record where it is held and when it goes again')}
                      onClick={() => {
                        if (block) setAlertDialog({ isOpen: true, message: block, type: 'info' });
                        else setShowRedeliver(true);
                      }}
                    >
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">{mode === 'edit' ? 'edit_calendar' : 'assignment_return'}</span>
                      </div>
                      <span className="btn-label">{mode === 'edit' ? 'Edit redelivery…' : 'Refused — redeliver…'}</span>
                    </button>
                  );
                })()}
                {/* A split's tonnage invoiced on the customer's other order
                    that day as one line (utils/tonnageAdditions). */}
                {(() => {
                  const { mode, block } = addToOrderActionFor(order, user);
                  return (
                    <button data-write-action={mode === 'view' ? undefined : true} data-read-safe={mode === 'view' ? true : undefined}
                      className={`btn-slideout ${block ? 'disabled' : ''}`}
                      title={block || (mode === 'view'
                        ? "What this order shares with the customer's other order — take it back out there"
                        : "Invoice this split's tonnage on the customer's other order that day — one invoice, one line")}
                      onClick={() => {
                        if (block) setAlertDialog({ isOpen: true, message: block, type: 'info' });
                        else setShowAddToOrder(true);
                      }}
                    >
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">call_merge</span>
                      </div>
                      <span className="btn-label">{mode === 'view' ? ADDED_TONNAGE_LABEL : ADD_TO_ORDER_LABEL}</span>
                    </button>
                  );
                })()}
                <button data-write-action
                  className={`btn-slideout ${!canInvoice ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!canInvoice) {
                      setAlertDialog({ isOpen: true, message: invoiceBlock, type: 'info' });
                    } else {
                      onClose && onClose();
                      startInvoiceFromOrder(appNav, order.id);
                    }
                  }}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">receipt</span>
                  </div>
                  <span className="btn-label">Create Invoice</span>
                </button>
                {isInvoiced && (
                  <button data-write-action
                    className="btn-slideout"
                    title="The changes the invoice still needs for the supplied quantities"
                    onClick={() => setShowInvoiceUpdate(true)}
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">receipt_long</span>
                    </div>
                    <span className="btn-label">Update invoice from supplied qty…</span>
                  </button>
                )}
                {/* The words / item codes / supplier reference the invoice
                    still differs from the order on (corrections after it was
                    invoiced). Always in its place; disabled until invoiced. */}
                <button data-write-action
                  className="btn-slideout"
                  disabled={!isInvoiced}
                  title={isInvoiced
                    ? 'Offer the invoice the order\'s current words, item codes and supplier reference where they differ'
                    : 'The order is not invoiced yet — its invoice will be made from the order as it stands'}
                  onClick={() => { if (isInvoiced) setShowInvoiceFromOrder(true); }}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">sync_alt</span>
                  </div>
                  <span className="btn-label">Update invoice from order…</span>
                </button>
              </div>
            );
          })()}
          </>
        ) : (
          <div className="modal-body">
            <p>Order not found</p>
          </div>
        )}
      </div>

      {sendingOrder && (
        <SendToSupplierFlow
          linkedPos={sendingOrder.purchase_orders}
          orderNumber={sendingOrder.order_number}
          user={user}
          onSent={(message) => {
            setAlertDialog({ isOpen: true, message, type: 'success' });
            fetchOrderDetails();
          }}
          onOpenInPOSearch={onOpenOrderInPOSearch}
          onClose={() => setSendingOrder(null)}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        }}
        onCancel={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })}
      />

      {/* Audit History Modal */}
      <AuditHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        entityType="customer_order"
        entityId={order?.id}
        entityIdentifier={order?.order_number}
      />

      {changeRequestOpen && order && (
        <RepChangeRequestModal
          kind="amend_order"
          targetId={order.id}
          onClose={() => setChangeRequestOpen(false)}
        />
      )}


      {/* Add Supplied Quantity Modal */}
      <AddSuppliedQuantityModal
        user={user}
        isOpen={showSuppliedQty}
        orderId={order?.id}
        onClose={() => setShowSuppliedQty(false)}
        onSuccess={() => {
          setShowSuppliedQty(false);
          setLoading(true);
          fetchOrderDetails();
        }}
      />

      {showInvoiceFromOrder && order && (
        <UpdateInvoiceFromOrderDialog
          orderId={order.id}
          onClose={() => setShowInvoiceFromOrder(false)}
          onDone={(message, type) => setAlertDialog({ isOpen: true, type: type || 'success', message })}
        />
      )}

      {showInvoiceUpdate && order && (
        <UpdateInvoiceFromSuppliedDialog
          baseUrl={`${API_URL}/api/orders/customer-orders/${order.id}/invoice-proposals`}
          onClose={() => setShowInvoiceUpdate(false)}
          onDone={(message, type) => setAlertDialog({ isOpen: true, type: type || 'success', message })}
          onNavigate={appNav ? (menu) => { onClose && onClose(); appNav(menu); } : null}
        />
      )}

      {/* Split Order Modal */}
      <SplitOrderModal
        isOpen={showSplitOrder}
        orderId={order?.id}
        onClose={() => setShowSplitOrder(false)}
        onSuccess={() => {
          setShowSplitOrder(false);
          setLoading(true);
          fetchOrderDetails();
        }}
      />

      {showAddToOrder && order && (
        <AddToOrderModal
          orderId={order.id}
          onClose={() => { setShowAddToOrder(false); setLoading(true); fetchOrderDetails(); }}
          onDone={(message) => message && setAlertDialog({ isOpen: true, message, type: 'success' })}
        />
      )}
      {showRedeliver && order && (
        <RedeliverOrderModal
          orderId={order.id}
          onClose={() => { setShowRedeliver(false); setLoading(true); fetchOrderDetails(); }}
          onDone={(message) => message && setAlertDialog({ isOpen: true, message, type: 'success' })}
        />
      )}

      {showDivert && order && (
        <DivertOrderFlow
          orderId={order.id}
          user={user}
          onClose={() => { setShowDivert(false); setLoading(true); fetchOrderDetails(); }}
        />
      )}

      {editingSplitNotes && order && (
        <SplitNotesModal
          orderId={order.id}
          orderNumber={order.order_number}
          notes={(order.parent_order_id ? order.split_notes : order.child_split_notes) || ''}
          onClose={() => setEditingSplitNotes(false)}
          onSaved={fetchOrderDetails}
        />
      )}

      {/* Purchase Order view modal — opened in place when a PO chip is clicked */}
      {viewingPOId && (
        <PurchaseOrderModal
          purchaseOrderId={viewingPOId}
          user={user}
          // Without this the PO modal drops its whole footer — "Find in Search
          // POs" is what renders it — so a PO opened from here lost the button
          // and the Send to Supplier beside it.
          onOpenInSearch={onOpenPOInSearch}
          onClose={() => setViewingPOId(null)}
        />
      )}
    </div>,
    document.body
  );
}

export default CustomerOrderModal;
